import { useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  SnackbarContext,
  SnackbarVariant,
  getErrorMessage,
  useEventCallback,
} from '@eas/common-web';
import {
  createSettings,
  deleteSettings,
  fetchSettingsByTag,
} from './named-settings-api';
import { NamedSettingsContext } from './named-settings-context';
import { NamedSettings } from './named-settings-types';

export function useNamedSettings(url: string, adminPermission?: string) {
  const intl = useIntl();
  const { showSnackbar } = useContext(SnackbarContext);
  const [loading, setLoading] = useState(false);

  const getNamedSettings = useEventCallback(async (tag: string) => {
    try {
      const response = await fetchSettingsByTag(url, tag).response;

      let settings: NamedSettings[];

      try {
        const json = await response.json();
        settings = json;
        console.log({ json });
      } catch {
        settings = [];
      }

      setLoading(false);

      return settings;
    } catch (err) {
      setLoading(false);
      if ((err as any).name !== 'AbortError') {
        const errorDetail = getErrorMessage(err as any);

        const message = intl.formatMessage(
          {
            id: 'EAS_NAMED_SETTINGS_MSG_LOAD_ERROR',
            defaultMessage: 'Chyba načtení nastavení: {detail}',
          },
          { detail: errorDetail }
        );

        showSnackbar(message, SnackbarVariant.ERROR);

        throw err;
      }

      return [];
    }
  });

  const saveNamedSettings = useEventCallback(
    async (settings: NamedSettings) => {
      try {
        const result: NamedSettings = await createSettings(
          url,
          settings
        ).json();

        setLoading(false);

        return result;
      } catch (err) {
        setLoading(false);
        if ((err as any).name !== 'AbortError') {
          const errorDetail = getErrorMessage(err as any);

          const message = intl.formatMessage(
            {
              id: 'EAS_NAMED_SETTINGS_MSG_SAVE_ERROR',
              defaultMessage: 'Chyba uložení nastavení: {detail}',
            },
            { detail: errorDetail }
          );

          showSnackbar(message, SnackbarVariant.ERROR);

          throw err;
        }

        throw err;
      }
    }
  );

  const deleteNamedSettings = useEventCallback(async (id: string) => {
    try {
      await deleteSettings(url, id).none();

      setLoading(false);
    } catch (err) {
      setLoading(false);
      if ((err as any).name !== 'AbortError') {
        const errorDetail = getErrorMessage(err as any);

        const message = intl.formatMessage(
          {
            id: 'EAS_NAMED_SETTINGS_MSG_CLEAR_ERROR',
            defaultMessage: 'Chyba mazání nastavení: {detail}',
          },
          { detail: errorDetail }
        );

        showSnackbar(message, SnackbarVariant.ERROR);

        throw err;
      }
    }
  });

  const context: NamedSettingsContext = useMemo(
    () => ({
      getNamedSettings,
      saveNamedSettings,
      deleteNamedSettings,
      adminPermission,
    }),
    [getNamedSettings, saveNamedSettings, deleteNamedSettings, adminPermission]
  );

  return { context, loading };
}
